import type { App } from '../app.ts';
import type { AppState, Closure, GradientState } from '../types.ts';
import { $ } from './dom.ts';
import { GRADIENT_PRESETS } from '../gradient/presets.ts';
import { RampCache, colorAt, cssRgb, hexToRgb, rgbToHex } from '../gradient/ramp.ts';
import { rampDiagnostics } from '../gradient/diag.ts';

export function mountGradientEditor(app: App): void {
  const gradGroup = $('gradGroup');
  const selPreset = $<HTMLSelectElement>('selPreset');
  const gradPreview = $<HTMLCanvasElement>('gradPreview');
  const stopRow = $('stopRow');
  const rngRepeat = $<HTMLInputElement>('rngRepeat');
  const chkAutoCycles = $<HTMLInputElement>('chkAutoCycles');
  const lblRepeat = $('lblRepeat');
  const lblRepeatName = $('lblRepeatName');
  const chkRevGrad = $<HTMLInputElement>('chkRevGrad');
  const chkPingPong = $<HTMLInputElement>('chkPingPong');
  const chkHard = $<HTMLInputElement>('chkHard');
  const selClosure = $<HTMLSelectElement>('selClosure');
  const rngRot = $<HTMLInputElement>('rngRot');
  const lblRot = $('lblRot');
  const cycleDiag = $('cycleDiag');
  const txtStops = $<HTMLInputElement>('txtStops');
  const rampOnly = [$('rampOnly'), $('rampOnly2')];
  const helixHint = $('helixHint');
  const ramp = new RampCache();

  const patchGrad = (p: Partial<GradientState>) => app.store.patch({ grad: { ...app.state.grad, ...p } });
  const setStops = (stops: string[]) => patchGrad({ stops, preset: 'custom' });

  const paintPreview = () => {
    const w = Math.max(1, gradPreview.clientWidth | 0);
    if (gradPreview.clientWidth === 0) return;
    const dpr = window.devicePixelRatio || 1;
    gradPreview.width = Math.round(w * dpr);
    gradPreview.height = Math.round(18 * dpr);
    const g = gradPreview.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const grad = app.effectiveGrad();
    const mode = app.state.colorMode;
    for (let x = 0; x < w; x++) {
      g.fillStyle = cssRgb(colorAt(w > 1 ? x / (w - 1) : 0, app.cyclic, grad, mode, ramp));
      g.fillRect(x, 0, 1, 18);
    }
  };

  let stopsKey = '';
  const syncStopInputs = (stops: string[]) => {
    const key = stops.join(',');
    if (key === stopsKey) return;
    stopsKey = key;
    stopRow.innerHTML = '';
    stops.forEach((hex, i) => {
      const inp = document.createElement('input');
      inp.type = 'color';
      inp.value = hex;
      inp.title = 'Stop ' + (i + 1);
      inp.style.cssText = 'width:30px;height:26px;padding:0;border:1px solid var(--bd);border-radius:5px;background:none;cursor:pointer';
      inp.addEventListener('input', () => {
        const next = app.state.grad.stops.slice();
        next[i] = inp.value;
        setStops(next);
      });
      stopRow.appendChild(inp);
    });
  };

  // Worth checking continuously once the curve is a cycle: a palette seam is
  // invisible in a swatch but obvious in the plate.
  const updateDiag = (s: AppState, grad: GradientState) => {
    const cyc = app.cyclic;
    if (s.colorMode === 'helix') {
      cycleDiag.innerHTML = cyc
        ? 'curve <b>closed cycle</b> &middot; mode <b>helix</b><br/>helix is one-to-one, so it <b>cannot close</b>: expect a lightness seam at the basepoint'
        : 'curve <b>open path</b> &middot; mode <b>helix</b> (one-to-one along the tour)';
      return;
    }
    const d = rampDiagnostics(ramp.get(grad, cyc));
    cycleDiag.innerHTML =
      'curve <b>' + (cyc ? 'closed cycle' : 'open path') + '</b> &middot; stops <b>' + grad.stops.length + '</b><br/>' +
      'closure <b>' + (cyc ? grad.closure : 'n/a') + '</b> &middot; seam <b>' + d.seam.toFixed(2) + '</b> &middot; muddy <b>' + d.muddy + '</b>/' + d.N;
  };

  const sync = () => {
    const s = app.state;
    const grad = app.effectiveGrad();
    selPreset.value = GRADIENT_PRESETS[grad.preset] ? grad.preset : 'custom';
    syncStopInputs(grad.stops);
    rngRepeat.value = String(grad.repeat);
    lblRepeat.textContent = String(grad.repeat);
    chkAutoCycles.checked = grad.autoCycles;
    chkRevGrad.checked = grad.reverse;
    chkPingPong.checked = grad.pingpong;
    chkHard.checked = grad.hard;
    selClosure.value = grad.closure;
    rngRot.value = String(grad.rotation);
    lblRot.textContent = grad.rotation.toFixed(3);
    if (document.activeElement !== txtStops) txtStops.value = grad.stops.join(', ');
    const helix = s.colorMode === 'helix';
    gradGroup.style.display = s.colorMode === 'solid' ? 'none' : '';
    for (const e of rampOnly) e.style.display = helix ? 'none' : '';
    helixHint.style.display = helix ? '' : 'none';
    lblRepeatName.textContent = helix ? 'Hue turns' : 'Cycles along curve';
    updateDiag(s, grad);
    paintPreview();
  };

  selPreset.addEventListener('change', () => {
    const p = selPreset.value;
    if (p !== 'custom' && GRADIENT_PRESETS[p]) patchGrad({ preset: p, stops: GRADIENT_PRESETS[p].slice() });
  });
  $('btnStopAdd').addEventListener('click', () => {
    const stops = app.state.grad.stops;
    setStops(stops.concat([stops[stops.length - 1] || '#ffffff']));
  });
  $('btnStopDel').addEventListener('click', () => {
    const stops = app.state.grad.stops;
    if (stops.length <= 2) return;
    setStops(stops.slice(0, -1));
  });
  txtStops.addEventListener('change', () => {
    const parts = txtStops.value.split(/[,\s]+/).filter(Boolean).map((x) => rgbToHex(hexToRgb(x)));
    if (parts.length >= 2) setStops(parts);
    else txtStops.value = app.state.grad.stops.join(', ');
  });
  rngRepeat.addEventListener('input', () => patchGrad({ repeat: parseInt(rngRepeat.value, 10) || 1, autoCycles: false }));   // manual override wins
  chkAutoCycles.addEventListener('change', () => patchGrad({ autoCycles: chkAutoCycles.checked }));
  chkRevGrad.addEventListener('change', () => patchGrad({ reverse: chkRevGrad.checked }));
  chkPingPong.addEventListener('change', () => patchGrad({ pingpong: chkPingPong.checked }));
  chkHard.addEventListener('change', () => patchGrad({ hard: chkHard.checked }));
  selClosure.addEventListener('change', () => patchGrad({ closure: selClosure.value as Closure }));
  rngRot.addEventListener('input', () => patchGrad({ rotation: parseFloat(rngRot.value) || 0 }));
  const rotBy = (dir: number) => {
    const g = app.state.grad;
    const n = Math.max(2, g.stops.length);
    patchGrad({ rotation: (((g.rotation + dir / n) % 1) + 1) % 1 });
  };
  $('btnRotL').addEventListener('click', () => rotBy(-1));
  $('btnRotR').addEventListener('click', () => rotBy(+1));

  window.addEventListener('resize', paintPreview);
  app.store.subscribe(sync);
  app.listeners.add(sync);   // cyclic flag changes with geometry
  sync();
}
